import { Badge } from "antd";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { BellFilled, BellOutlined } from "@ant-design/icons";
import { useSignalR } from "../../providers/SignalRProvider";
import newOrderSound from "../../assets/sounds/orderSound.mp3";
import { getDomainFromLocalStorage } from "../../utils/commonfunction";

const NewOrderNotification = () => {
  const navigate = useNavigate();
  const { connection, onReconnect } = useSignalR();

  const [pendingCount, setPendingCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasNewOrders, setHasNewOrders] = useState(false);

  const soundIntervalRef = useRef(null);
  const animationIntervalRef = useRef(null);
  const isPlayingRef = useRef(false); // Track if sound is currently playing
  const audioRef = useRef(null); // Store audio instance

  // Fetch all orders to check pending status
  const fetchOrdersStatus = (conn) => {
    const userId = Number(localStorage.getItem("USER_ID"));
    const domain = getDomainFromLocalStorage();

    if (!domain || !userId || !conn || conn.state !== "Connected") return;

    conn
      .invoke("OrderHistoryRequest", domain, userId, "AllOrders")
      .catch((err) => console.error("Error fetching orders:", err));
  };

  // Play sound helper - only if not already playing
  const playSound = () => {
    // Don't play if already playing
    if (isPlayingRef.current) {
      return;
    }

    isPlayingRef.current = true;

    const audio = new Audio(newOrderSound);
    audioRef.current = audio;

    audio.play().catch((err) => {
      console.error("Error playing sound:", err);
      isPlayingRef.current = false;
    });

    // Reset flag when sound ends
    audio.onended = () => {
      isPlayingRef.current = false;
      audioRef.current = null;
    };

    // Fallback: reset after max duration (in case onended doesn't fire)
    setTimeout(() => {
      isPlayingRef.current = false;
      audioRef.current = null;
    }, 5000);
  };

  // Start animation only if not already animating
  const startAnimation = () => {
    if (!isAnimating) {
      setIsAnimating(true);

      // Stop animation after 5 seconds
      setTimeout(() => {
        setIsAnimating(false);
      }, 5000);
    }
  };

  // Start continuous notification
  const startContinuousNotification = () => {
    // Clear any existing intervals
    stopContinuousNotification();

    // Play sound and start animation immediately
    playSound();
    startAnimation();

    // Play sound every 5 seconds (adjust as needed)
    soundIntervalRef.current = setInterval(() => {
      playSound();
    }, 5000); // 5 seconds

    // Restart animation every 5 seconds (sync with sound)
    animationIntervalRef.current = setInterval(() => {
      startAnimation();
    }, 5000);
  };

  // Stop continuous notification
  const stopContinuousNotification = () => {
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }

    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    isPlayingRef.current = false;
    setIsAnimating(false);
  };

  // Listen for all orders response
  const handleAllOrders = (data) => {
    if (data.dataPayload?.Orders) {
      // Count pending orders
      const pending = data.dataPayload.Orders.filter(
        (order) => (order.Status || order.status) === "Pending",
      ).length;

      setPendingCount(pending);
      setHasNewOrders(pending > 0);

      // Start or stop notification based on pending count
      if (pending > 0) {
        // Only start if not already running
        if (!soundIntervalRef.current) {
          startContinuousNotification();
        }
      } else {
        stopContinuousNotification();
      }
    }
  };

  // Listen for new orders
  const handleNewOrder = (neworder) => {
    console.log("checking what is coming on new order event", neworder)
    // Play sound immediately for new order (only if not already playing)
    playSound();
    startAnimation();

    // Refetch all orders to update pending count
    fetchOrdersStatus(connection);
  };

  // Listen for order status updates
  const handleOrderStatusUpdate = (response) => {
    console.log("checking what is coming in order status update event", response)
    // Refetch all orders when status changes
    fetchOrdersStatus(connection);
  };
  

  useEffect(() => {
    if (!connection || connection.state !== "Connected") return;

    connection.on("AllOrders", handleAllOrders);
    connection.on("NewOrder", handleNewOrder);
    connection.on("OrderStatusUpdate", handleOrderStatusUpdate);

    // Initial fetch
    fetchOrdersStatus(connection);

    // Refetch on reconnect
    const unsubscribe = onReconnect(() => {
      fetchOrdersStatus(connection);
    });

    return () => {
      connection.off("AllOrders", handleAllOrders);
      connection.off("NewOrder", handleNewOrder);
      connection.off("OrderStatusUpdate", handleOrderStatusUpdate);
      stopContinuousNotification();
      unsubscribe();
    };
  }, [connection]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopContinuousNotification();
    };
  }, []);

  const handleClick = () => {
    // Navigate to orders page
    navigate("/customerorders");
  };

  return (
    <>
      <button
        onClick={handleClick}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60px",
          width: "60px",
          padding: 0,
          border: "none",
          background: "transparent",
          cursor: "pointer",
          outline: "none",
        }}
      >
        <Badge
          count={pendingCount}
          offset={[-3, 3]}
          style={{
            backgroundColor: "#ff4d4f",
          }}
        >
          {hasNewOrders ? (
            <BellFilled
              style={{
                fontSize: "32px",
                color: "#FFFFFF",
                animation: isAnimating
                  ? "ring 1s ease-in-out infinite"
                  : "none",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
              }}
            />
          ) : (
            <BellOutlined
              style={{
                fontSize: "32px",
                color: "#FFFFFF",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
              }}
            />
          )}
        </Badge>
      </button>

      <style>
        {`
          @keyframes ring {
            0% { transform: rotate(0deg); }
            10% { transform: rotate(-20deg); }
            20% { transform: rotate(20deg); }
            30% { transform: rotate(-20deg); }
            40% { transform: rotate(20deg); }
            50% { transform: rotate(0deg); }
            100% { transform: rotate(0deg); }
          }
        `}
      </style>
    </>
  );
};

export default NewOrderNotification;
